import React from 'react';
import LateralPanel from '../../../../../muiComponents/LateralPanel';
import IconButton from '../../../../../muiComponents/IconButton';
import useTranslate from '../../../../../customHooks/useTranslate';
import { commonIcons } from '../../../../../utils/commonVariables/commonIcons';
import { ScraperServiceStatus } from '../../../types/scrapedServicesTypes';
import '../../../stylesheets/service-scraper.scss';
import ServiceCategoryScraperSection from './ServiceCategoryScraperSection';
import LoadingServiceScraperPanelContent from './LoadingServiceScraperPanelContent';
import NoScrapedServices from './NoScrapedServices';
import Button from '../../../../../muiComponents/Button';
import { useServiceScraperPanel } from '../../../contexts/ServiceScraperPanelContext';
import { useZitoneoHotelPresentation } from '../../../contexts/ZitoneoContentContext';
import { useZitoneoUI } from '../../../contexts/ZitoneoUIContext';

const ServiceScraperPanel = () => {
    const translate = useTranslate();
    const {
        scrapedData,
        isOpen,
        isFullScreen,
        closePanel,
        toggleFullScreen,
        selectAll,
        clearSelection,
        saveChanges,
        isLoadingPanel,
    } = useServiceScraperPanel();
    const { hotel } = useZitoneoHotelPresentation();
    const { visibleLanguage } = useZitoneoUI();

    const services = scrapedData.flatMap(category => category.Services);
    const hasServices = services.length > 0;
    const areServiceActionsDisabled = isLoadingPanel || !hasServices;
    const newServices = services.filter(
        service => service.Status === ScraperServiceStatus.NEW
    );
    const existingServices = services.filter(
        service => service.Status === ScraperServiceStatus.EXISTING
    );

    return (
        <LateralPanel
            open={isOpen}
            onClose={closePanel}
            className={`service-scraper__panel ${
                isFullScreen ? '--full-width' : '--xl-width'
            }`}
            aria-labelledby='service-scraper-panel-title'
        >
            <header className='service-scraper__header flex-column gap-10'>
                <div className='flex flex-between flex-align-center border-color-bottom'>
                    <h2
                        id='service-scraper-panel-title'
                        className='lateral-panel__title'
                    >
                        {translate('autoimport_with_ai')}
                    </h2>
                    <div className='flex gap-10'>
                        <IconButton
                            iconName={
                                isFullScreen
                                    ? commonIcons.EXIT_FULL_SCREEN
                                    : commonIcons.FULL_SCREEN
                            }
                            onClick={toggleFullScreen}
                            aria-label={translate(
                                isFullScreen
                                    ? 'exit_full_screen'
                                    : 'full_screen'
                            )}
                            disabled={isLoadingPanel}
                        />
                        <IconButton
                            iconName={commonIcons.CLOSE}
                            onClick={closePanel}
                            aria-label={translate('close')}
                            disabled={isLoadingPanel}
                        />
                    </div>
                </div>

                <div className='flex flex-between flex-align-center'>
                    <div className='service-scraper__summary'>
                        <p>
                            <span className='--all'>{services.length}</span>{' '}
                            {translate('services')}
                        </p>
                        {'  ·  '}
                        <p>
                            <span className='--new'>{newServices.length}</span>{' '}
                            {translate('new')}
                        </p>
                        {'  ·  '}
                        <p>
                            <span className='--existing'>
                                {existingServices.length}
                            </span>{' '}
                            {translate('existing')}
                        </p>
                    </div>
                    <div className='flex gap-10'>
                        <Button
                            type='button'
                            startIcon={'CheckBox'}
                            onClick={selectAll}
                            disabled={areServiceActionsDisabled}
                        >
                            {translate('select_all')}
                        </Button>
                        <Button
                            type='button'
                            color='secondary'
                            startIcon={'IndeterminateCheckBoxOutlined'}
                            onClick={clearSelection}
                            disabled={areServiceActionsDisabled}
                        >
                            {translate('clear_selection')}
                        </Button>
                    </div>
                </div>
            </header>
            <main className='service-scraper__main' aria-busy={isLoadingPanel}>
                {isLoadingPanel ? (
                    <LoadingServiceScraperPanelContent />
                ) : hasServices ? (
                    scrapedData
                        .filter(category => category.Services.length)
                        .map(category => (
                            <ServiceCategoryScraperSection
                                key={category.IdServiceCategory}
                                category={category}
                            />
                        ))
                ) : (
                    <NoScrapedServices />
                )}
            </main>
            <footer className='service-scraper__footer'>
                <Button
                    type='button'
                    variant='outlined'
                    onClick={closePanel}
                    disabled={isLoadingPanel}
                >
                    {translate('close')}
                </Button>
                <Button
                    type='button'
                    variant='contained'
                    onClick={() => {
                        if (visibleLanguage) {
                            void saveChanges(hotel.id, hotel.idHotel);
                        }
                    }}
                    disabled={areServiceActionsDisabled}
                >
                    {translate('save')}
                </Button>
            </footer>
        </LateralPanel>
    );
};

export default ServiceScraperPanel;
